import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TestimonialCard from './components/TestimonialCard';
import StatisticsBar from './components/StatisticsBar';
import { Testimonial } from './types';

// Extend Testimonial type inline to support category property
interface ExtendedTestimonial extends Testimonial {
  category: string;
}

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const testimonials: ExtendedTestimonial[] = [
    {
      id: 1,
      name: t("testimonials.items.abdullah.name"),
      role: t("testimonials.items.abdullah.role"),
      company: t("testimonials.items.abdullah.company"),
      image: "https://ui-avatars.com/api/?name=عبدالله+ف&background=020024&color=34b7d1&size=128",
      alt: "عبدالله ف.",
      rating: 5,
      content: t("testimonials.items.abdullah.content"),
      projectType: t("testimonials.items.abdullah.projectType"),
      companyLogo: "",
      companyLogoAlt: "",
      date: "2024",
      category: "enterprise"
    },
    {
      id: 2,
      name: t("testimonials.items.sara.name"),
      role: t("testimonials.items.sara.role"),
      company: t("testimonials.items.sara.company"),
      image: "https://ui-avatars.com/api/?name=سارا+أ&background=34b7d1&color=020024&size=128",
      alt: "سارا أ.",
      rating: 5,
      content: t("testimonials.items.sara.content"),
      projectType: t("testimonials.items.sara.projectType"),
      companyLogo: "",
      companyLogoAlt: "",
      date: "2024",
      category: "ecommerce"
    }
  ];


  const filters = [
  { id: 'all', label: t('testimonials.filters.all'), icon: 'Grid3x3' },
  { id: 'ecommerce', label: t('testimonials.filters.ecommerce'), icon: 'ShoppingCart' },
  { id: 'saas', label: t('testimonials.filters.saas'), icon: 'Cloud' },
  { id: 'enterprise', label: t('testimonials.filters.enterprise'), icon: 'Building2' }];


  const filteredTestimonials = activeFilter === 'all' ?
  testimonials :
  testimonials.filter((t) => t.category === activeFilter);

  return (
    <>
      <Helmet>
        <title>{t('testimonials.successStories')} - {t('hero.name')}</title>
        <meta
          name="description"
          content={t('testimonials.subtitle')} />
        <meta name="keywords" content="client testimonials, developer reviews, Laravel projects, React development, full-stack success stories" />
      </Helmet>

      <div className="min-h-screen gradient-background">
        <Header />

        <main className="pt-24 pb-20 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-6">
                <Icon name="MessageSquare" size={20} color="var(--color-accent)" />
                <span className="text-sm font-medium text-accent">{t('testimonials.successStories')}</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {t('testimonials.title')}
                <span className="block mt-2 text-accent glow-text">
                  {t('testimonials.workingTogether')}
                </span>
              </h1>

              <p className="text-lg md:text-xl text-white/65 max-w-3xl mx-auto leading-relaxed">
                {t('testimonials.subtitle')}
              </p>
            </div>

            <StatisticsBar />

            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {filters.map((filter) =>
              <Button
                key={filter.id}
                variant={activeFilter === filter.id ? 'default' : 'outline'}
                size="default"
                className={activeFilter === filter.id ? 'gradient-cta' : ''}
                  iconName={filter.icon as any}
                iconPosition="left"
                iconSize={18}
                onClick={() => setActiveFilter(filter.id)}>

                  {filter.label}
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
              {filteredTestimonials.map((testimonial, index) =>
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                index={index} />

              )}
            </div>

            <div className="bg-gradient-accent rounded-2xl p-8 md:p-12 text-center shadow-brand-lg">
              <div className="max-w-3xl mx-auto">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm mb-6">
                  <Icon name="Sparkles" size={32} color="white" />
                </div>

                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {t('testimonials.cta.title')}
                </h2>

                <p className="text-lg text-white/90 mb-8">
                  {t('testimonials.cta.subtitle')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-white/20 hover:text-black/20 text-primary hover:bg-white/90"
                    iconName="MessageCircle"
                    iconPosition="left"
                    iconSize={20}
                    onClick={() => window.location.href = '/contact-section'}>

                    {t('testimonials.cta.startProject')}
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white/10"
                    iconName="Eye"
                    iconPosition="left"
                    iconSize={20}
                    onClick={() => window.location.href = '/portfolio-showcase'}>

                    {t('testimonials.cta.viewPortfolio')}
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="glass-card text-center p-6 rounded-xl">
                <Icon name="Shield" size={36} color="var(--color-accent)" className="mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  {t('testimonials.features.verified')}
                </h3>
                <p className="text-sm text-white/55">
                  {t('testimonials.features.verifiedDesc')}
                </p>
              </div>

              <div className="glass-card text-center p-6 rounded-xl">
                <Icon name="Lock" size={36} color="var(--color-accent)" className="mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  {t('testimonials.features.nda')}
                </h3>
                <p className="text-sm text-white/55">
                  {t('testimonials.features.ndaDesc')}
                </p>
              </div>

              <div className="glass-card text-center p-6 rounded-xl">
                <Icon name="Award" size={36} color="var(--color-accent)" className="mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  {t('testimonials.features.quality')}
                </h3>
                <p className="text-sm text-white/55">
                  {t('testimonials.features.qualityDesc')}
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);
};

export default TestimonialsSection;